<template>
  <div>
    <!-- 支払方法内訳 -->
    <div class="table-header">
      <h3>
        支払方法内訳
        <span v-if="showCaptureModeNote"
            class="capture-mode-note color-warning">
          決済予約をする支払方法にチェックを入れて下さい。
        </span>
        <!-- ボタングループ -->
        <span class="table-header-btns">
          <span v-if="showSetPaymentAmount">
            <el-button size="small"
                type="danger"
                plain
                :v-show="showAddRow"
                :disabled="disableSetPaymentAmount"
                @click="setPaymentAmount">
              注文合計金額をセットする
            </el-button>
          </span>
          <span v-if="showDropdown">
            <el-dropdown @command="addRow">
              <el-button size="small"
                  :v-show="showAddRow"
                  :disabled="disableAddRow">
                行追加<i class="el-icon-arrow-down el-icon--right" />
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item>支払種別</el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </span>
        </span>
      </h3>
    </div>
    <table>
      <tbody is="transition-group"
          :css="false"
          class="item-detail-payment-type"
          @before-enter="$_tableRowAnimator_beforeEnter"
          @before-leave="$_tableRowAnimator_beforeLeave"
          @enter="$_tableRowAnimator_enter"
          @leave="$_tableRowAnimator_leave">
        <tr key="header">
          <th v-for="(l, index) in paymentsList"
              v-show="$isFunctionShown(l.funcId)"
              :key="index"
              :width="l.width"
              :class="l.headerClass">
            {{ l.label }}
          </th>
        </tr>
        <OrderDetailPaymentTypeRow v-for="displayItem in rejectedZeroHoldOrderPayments"
            :key="getUuid(displayItem)"
            :payment="displayItem"
            :order-total-price="orderTotalPrice"
            :amount="totalPrice()"
            :origin-data="originData"
            :mode="mode"
            :validate-scope="validateScope"
            @removeRow="removeRow(displayItem)" />
        <template v-for="l in totalRows">
          <tr v-if="showTotal"
              :key="l.key"
              class="orderdetail-line">
            <template v-for="p in l.properties">
              <td v-show="p.show == null || p.show"
                  :key="getUuid(p)"
                  :colspan="p.colspan"
                  :class="p.class">
                <BaseCell
                    :properties="p"
                    :display-item="{}"
                    :site-code="siteCode" />
              </td>
            </template>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.capture-mode-note {
  float: right;
  font-size: 14px;
}
</style>

<script>
import _ from 'lodash'
import { v4 as uuid } from 'uuid';
import { mapGetters } from 'vuex'

import x from '@/components/mixins/mixin'
import tableRowAnimator from '@/components/mixins/tableRowAnimator'
import math from '@/utils/math'
import * as define from '@/define'
import BaseCell, { ELEMENT_TYPE } from '@/components/molecules/BaseCell'
import OrderDetailPaymentTypeRow from '@/components/pages/order/OrderDetailPaymentTypeRow'

export default {
  name: 'OrderDetailPaymentType',
  components: {
    BaseCell,
    OrderDetailPaymentTypeRow
  },
  mixins: [x, tableRowAnimator],
  inject: ['parentValidator'],
  props: {
    originData: {
      type: Object,
      default: () => []
    },
    mode: {
      type: Number,
      default: define.ORDER_MODE.NORMAL
    },
    validateScope: {
      type: String,
      default: null
    }
  },
  computed: {
    ...mapGetters('OrderDetail', {
      orderDetail: 'orderDetail',
      siteCode: 'siteCode'
    }),
    rejectedZeroHoldOrderPayments () {
      const zeroHoldPayments = _.filter(this.originData.payments, payment => {
        return +payment.amount === 0 && payment.paymentMethod.methodType === define.PAYMENT_METHOD_TYPE.HOLD
      })
      return _.reject(this.orderDetail.payments, payment => {
        return _.includes(_.flatMap(zeroHoldPayments, 'id'), payment.id) && +payment.amount === 0
      })
    },
    paymentsList () {
      return this.mode === define.ORDER_MODE.CAPTURE ? [
        { label: '決済連携', width: 70, headerClass: 'center',
          funcId: define.PermissionKeys.Order.Detail.OrderInfo.PaymentTypeBreakdown.PAYMENT_LINKAGE },
        { label: '支払処理対象', width: 100, headerClass: 'center' },
        { label: '支払状況', width: 100 },
        { label: '', width: 100 },
        { label: '支払方法' },
        { label: '取引ID' },
        { label: '金額', width: 140, headerClass: 'right' },
        { label: '', width: 20 }
      ] : [
        { label: '', width: 70, headerClass: 'center',
          funcId: define.PermissionKeys.Order.Detail.OrderInfo.PaymentTypeBreakdown.PAYMENT_LINKAGE },
        { label: '支払処理対象', width: 100, headerClass: 'center' },
        { label: '支払状況', width: 100 },
        { label: '', width: 100 },
        { label: '支払方法' },
        { label: '取引ID' },
        { label: '金額', width: 140, headerClass: 'right' },
        { label: '', width: 20 }
      ]
    },
    totalRows () {
      return [
        {
          key: 'total',
          properties: [
            { type: 'String', elementType: ELEMENT_TYPE.FIXED_TEXT, path: '合計', colspan: 6, class: { bold: true, right: true } },
            { type: 'Number', elementType: ELEMENT_TYPE.FUNCTION, path: this.totalPrice, class: { bold: true, right: true } },
            { path: null }
          ]
        },
        {
          key: 'total-difference',
          properties: [
            { type: 'String', elementType: ELEMENT_TYPE.FIXED_TEXT, path: '過不足金額', colspan: 6, class: { bold: true, right: true, difference: true } },
            { type: 'Number', elementType: ELEMENT_TYPE.FUNCTION, path: this.totalPriceDifference, class: { bold: true, right: true, difference: true } },
            { path: null }
          ]
        }
      ]
    },
    /** 金額セットボタン */
    showSetPaymentAmount () {
      return [define.ORDER_MODE.EDIT, define.ORDER_MODE.SHORTAGE, define.ORDER_MODE.RECEIVED, define.ORDER_MODE.EXCHANGE, define.ORDER_MODE.CHANGE_PAYMENT].includes(this.mode)
    },
    disableSetPaymentAmount () {
      return this.rejectedZeroHoldOrderPayments.length !== 1
    },
    showDropdown () {
      return this.$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.PaymentTypeBreakdown.ADD_PAYMENT_METHOD) &&
        [define.ORDER_MODE.EDIT, define.ORDER_MODE.SHORTAGE, define.ORDER_MODE.RECEIVED, define.ORDER_MODE.EXCHANGE, define.ORDER_MODE.CHANGE_PAYMENT].includes(this.mode)
    },
    showAddRow () {
      return this.$isFunctionShown(define.PermissionKeys.Order.Detail.OrderInfo.PaymentTypeBreakdown.ADD_PAYMENT_METHOD)
    },
    disableAddRow () {
      return this.$isFunctionDisabled(define.PermissionKeys.Order.Detail.OrderInfo.PaymentTypeBreakdown.ADD_PAYMENT_METHOD)
    },
    showCaptureModeNote () {
      return this.mode === define.ORDER_MODE.CAPTURE
    },
    showTotal () {
      return [define.ORDER_MODE.EDIT, define.ORDER_MODE.SHORTAGE, define.ORDER_MODE.RECEIVED, define.ORDER_MODE.EXCHANGE, define.ORDER_MODE.CHANGE_PAYMENT].includes(this.mode)
    },
    cancelAnimation () {
      return this.mode === define.ORDER_MODE.NORMAL
    },
    orderTotalPrice () {
      return _.chain(this.orderDetail)
        .get('orderTotalPrice.gross', 0)
        .thru(val => Number(val))
        .value()
    }
  },
  watch: {
    cancelAnimation () {
      this.$_tableRowAnimator_cancelAnimation = this.cancelAnimation
    }
  },
  created () {
    this.$validator = this.parentValidator
    this.$validator.extend('checkTotalPriceDifference', {
      getMessage: () => '過不足金が0になるよう調整して下さい。',
      validate: () => {
        return this.totalPrice() === this.orderTotalPrice
      }
    })
  },
  mounted () {
    this.$_tableRowAnimator_cancelAnimation = this.cancelAnimation
  },
  methods: {
    // 支払合計金額
    totalPrice () {
      return _.chain(this.orderDetail)
        .get('payments')
        .map(val => val.amount)
        .thru(val => {
          const total = math.add(val, math.ROUND, 0)
          return Number.isNaN(total) ? '' : total
        })
        .value()
    },
    // 商品合計と支払い合計の差額
    totalPriceDifference () {
      const total = this.totalPrice()
      if (total === '') {
        return ''
      }
      const diff = math.subtract([total, _.get(this.orderDetail, 'orderTotalPrice.gross', 0)])
      return Number.isNaN(diff) ? '' : diff
    },
    /**
     * 行追加ボタンメソッド
     * @param {Number} index 値引行を追加する商品明細インデックス
     */
    addRow: function (index) {
      this.orderDetail.payments.push({
        paymentMethod: {
          code: ''
        },
        amount: 0,
        isPaid: this.orderDetail.isPaid, // 注文の is_paid と同値を設定する
        isProcessed: 1
      })
    },
    removeRow (payment) {
      const i = _.findIndex(this.orderDetail.payments, { paymentTypeListKey: payment.paymentTypeListKey })
      this.orderDetail.payments.splice(i, 1)
    },
    getUuid (obj) {
      if (this.mode === define.ORDER_MODE.CAPTURE) {
        return obj.uuid
      }
      if (!obj.paymentTypeListKey) {
        obj.paymentTypeListKey = uuid()
      }
      return obj.paymentTypeListKey
    },
    setPaymentAmount () {
      this.rejectedZeroHoldOrderPayments[0].amount = parseFloat(_.get(this.orderDetail, 'orderTotalPrice.gross', 0))
    }
  }
}
</script>
